import {
  Component, Input, OnChanges, OnDestroy,
  SimpleChanges, ViewChild, ElementRef, AfterViewInit, inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import * as echarts from 'echarts';
import { Subscription } from 'rxjs';
import { ThemeService } from '../themes/theme.service';
import { ThemeTokens } from '../themes/theme.model';

export type EChartType =
  'ec_bar' | 'ec_rose' | 'ec_radar' | 'ec_gauge' |
  'ec_funnel' | 'ec_treemap' | 'ec_scatter' | 'ec_line';

export type EChartCardConfig = {
  title: string;
  chartType: EChartType;
  data: Record<string, any>[];
  labelKey?: string;
  valueKey?: string;
  value2Key?: string;
};


const fmtK = (v: number) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(Math.round(v));
const fmtCO = (v: number) => Number(v).toLocaleString('es-CO');

@Component({
  selector: 'app-echart-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './echart-card.component.html',
  styleUrl: './echart-card.component.scss',
})
export class EchartCardComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() config!: EChartCardConfig;
  @ViewChild('chartEl') chartEl!: ElementRef<HTMLDivElement>;

  private chart: echarts.ECharts | null = null;
  private resizeObserver?: ResizeObserver;
  private viewReady = false;
  private themeSub!: Subscription;
  private theme!: ThemeTokens;

  private readonly themeSvc = inject(ThemeService);

  private get p(): string[] { return this.theme?.palette ?? [this.p[0],this.p[1],'#fac858','#ee6666','#73c0de','#3ba272','#fc8452','#9a60b4']; }
  private get grid(): string { return this.theme?.chartGrid ?? 'rgba(0,0,0,0.06)'; }
  private get axisColor(): string { return this.theme?.chartAxis ?? '#64748b'; }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.themeSub = this.themeSvc.theme$.subscribe(t => {
      this.theme = t;
      if (this.config?.data?.length) setTimeout(() => this.build());
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] && this.viewReady && this.config?.data?.length) setTimeout(() => this.build());
  }

  ngOnDestroy(): void {
    this.themeSub?.unsubscribe();
    this.resizeObserver?.disconnect();
    this.chart?.dispose();
  }

  get typeName(): string {
    const map: Record<string, string> = {
      ec_bar:     'Barras + Línea %',
      ec_rose:    'Rose / Nightingale',
      ec_gauge:   'Gauge',
      ec_radar:   'Radar',
      ec_funnel:  'Funnel',
      ec_treemap: 'Treemap',
      ec_scatter: 'Dispersión',
      ec_line:    'Línea',
    };
    return map[this.config?.chartType] ?? this.config?.chartType ?? '';
  }

  private build(): void {
    if (this.chart) { this.chart.dispose(); this.chart = null; this.resizeObserver?.disconnect(); }
    const el = this.chartEl.nativeElement;
    this.chart = echarts.init(el, null, { renderer: 'canvas' });
    this.chart.setOption(this.getOption());
    this.resizeObserver = new ResizeObserver(() => this.chart?.resize());
    this.resizeObserver.observe(el);
  }

  private getOption(): echarts.EChartsOption {
    switch (this.config.chartType) {
      case 'ec_bar':     return this.barOption();
      case 'ec_rose':    return this.roseOption();
      case 'ec_radar':   return this.radarOption();
      case 'ec_gauge':   return this.gaugeOption();
      case 'ec_funnel':  return this.funnelOption();
      case 'ec_treemap': return this.treemapOption();
      case 'ec_scatter': return this.scatterOption();
      case 'ec_line':    return this.lineOption();
      default:           return {};
    }
  }

  // ── Barras sólidas + valor encima + línea % si hay Porcentaje_CUMP ──────────
  private barOption(): echarts.EChartsOption {
    const { data, labelKey, valueKey, value2Key } = this.config;
    const hasPct = data.length > 0 && 'Porcentaje_CUMP' in data[0];
    const pctData = hasPct ? data.map(r => +(Number(r['Porcentaje_CUMP']) * 100).toFixed(1)) : [];

    const barLabel = {
      show: true, position: 'top' as const,
      fontSize: 10, fontWeight: 700 as const,
      formatter: ({ value }: any) => fmtK(Number(value)),
    };

    const series: any[] = [{
      name: valueKey, type: 'bar', barMaxWidth: 44,
      itemStyle: { color: (_p: any) => this.p[_p.dataIndex % this.p.length] },
      data: data.map(r => Number(r[valueKey!])),
      label: barLabel,
    }];

    if (value2Key) series.push({
      name: value2Key, type: 'bar', barMaxWidth: 44,
      itemStyle: { color: this.p[1] },
      data: data.map(r => Number(r[value2Key])),
      label: { ...barLabel, color: '#333' },
    });

    if (hasPct) series.push({
      name: '% Cumplimiento', type: 'line', yAxisIndex: 1,
      smooth: true, data: pctData,
      lineStyle: { color: '#ee6666', width: 2 },
      itemStyle: { color: '#ee6666' }, symbol: 'circle', symbolSize: 6,
      label: {
        show: true, position: 'top' as const, fontSize: 10,
        color: '#ee6666', fontWeight: 700 as const,
        formatter: ({ value }: any) => `${value}%`,
      },
    });

    const yBase: any = {
      type: 'value',
      axisLabel: { show: false, fontSize: 10, formatter: fmtK },
    };

    return {
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const arr: any[] = Array.isArray(params) ? params : [params];
          const label = arr[0].axisValue;
          const lines = arr.map((p: any) => {
            const val = p.seriesName === '% Cumplimiento'
              ? `${p.value}%` : fmtCO(p.value);
            return `${p.marker}${p.seriesName}: <b>${val}</b>`;
          });
          return `${label}<br/>${lines.join('<br/>')}`;
        },
      },
      legend: { bottom: 0, textStyle: { fontSize: 10 } },
      grid: { left: 50, right: hasPct ? 55 : 20, top: 36, bottom: 52, containLabel: true },
      xAxis: {
        type: 'category',
        data: data.map(r => String(r[labelKey!]).substring(0, 18)),
        axisLabel: { rotate: 35, fontSize: 10 },
      },
      yAxis: hasPct
        ? [yBase, {
            type: 'value', min: 0, max: 120, name: '%',
            nameTextStyle: { fontSize: 10 },
            axisLabel: { show: false, fontSize: 10, formatter: (v: number) => `${v}%` },
            splitLine: { show: false },
          }]
        : yBase,
      series,
    };
  }

  // ── Rose / Nightingale — valor + nombre en etiqueta ──────────────────────────
  private roseOption(): echarts.EChartsOption {
    const { data, labelKey, valueKey } = this.config;
    return {
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      legend: { bottom: 0, type: 'scroll', textStyle: { fontSize: 10 } },
      series: [{
        type: 'pie', radius: ['15%', '65%'], roseType: 'area', center: ['50%', '44%'],
        data: data.map((r, i) => ({
          name: String(r[labelKey!]).substring(0, 22),
          value: Number(r[valueKey!]),
          itemStyle: { color: this.p[i % this.p.length] },
        })),
        label: {
          show: true, fontSize: 10,
          formatter: ({ name, value, percent }: any) =>
            `${name.substring(0, 14)}\n${fmtCO(value)}  (${percent}%)`,
        },
        labelLine: { length: 10, length2: 8 },
        emphasis: { itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.3)' } },
      }],
    };
  }

  // ── Radar — muestra valor en cada vértice ────────────────────────────────────
  private radarOption(): echarts.EChartsOption {
    const { data, labelKey, valueKey } = this.config;
    const slice = data.slice(0, 8);
    const vals  = slice.map(r => Number(r[valueKey!]));
    const max   = Math.max(...vals) * 1.15 || 1;
    return {
      tooltip: {
        trigger: 'item',
        formatter: ({ name, value }: any) => {
          const lines = (value as number[]).map((v, i) =>
            `${slice[i]?.[labelKey!]}: <b>${fmtCO(v)}</b>`
          );
          return `${name}<br/>${lines.join('<br/>')}`;
        },
      },
      radar: {
        indicator: slice.map(r => ({ name: String(r[labelKey!]).substring(0, 14), max })),
        splitArea: { areaStyle: { color: ['#f5f6ff', '#fff'] } },
        axisName: { fontSize: 10, color: '#555' },
      },
      series: [{
        type: 'radar',
        data: [{
          value: vals, name: valueKey,
          areaStyle: { color: 'rgba(84, 112, 198, 0.25)' },
          lineStyle: { color: this.p[0], width: 2 },
          itemStyle: { color: this.p[0] },
          label: {
            show: true, fontSize: 10, fontWeight: 700 as const, color: this.p[0],
            formatter: ({ value }: any) => fmtK(Number(value)),
          },
        }],
      }],
    };
  }

  // ── Gauge ────────────────────────────────────────────────────────────────────
  private gaugeOption(): echarts.EChartsOption {
    const { data, valueKey } = this.config;
    const raw   = data[0]?.[valueKey!] ?? '0';
    const pct   = parseFloat(String(raw).replace('%', '').trim());
    const color = pct >= 100 ? '#2e7d32' : pct >= 80 ? '#e65100' : '#c62828';
    return {
      series: [{
        type: 'gauge', startAngle: 220, endAngle: -40, min: 0, max: 120,
        splitNumber: 6, radius: '88%',
        axisLine: { lineStyle: { width: 14,
          color: [[0.67, '#c62828'], [0.83, '#e65100'], [1, '#2e7d32']] } },
        pointer: { itemStyle: { color } },
        axisTick: { distance: -20, length: 6, lineStyle: { color: '#fff', width: 1 } },
        splitLine: { distance: -28, length: 16, lineStyle: { color: '#fff', width: 2 } },
        axisLabel: { color: 'inherit', distance: 30, fontSize: 10, formatter: (v: number) => `${v}%` },
        detail: { valueAnimation: true, fontSize: 28, fontWeight: 700, color,
          formatter: '{value}%', offsetCenter: [0, '62%'] },
        title: { fontSize: 13, offsetCenter: [0, '86%'], color: '#555' },
        data: [{ value: pct, name: 'Cumplimiento' }],
      }],
    };
  }

  // ── Funnel — nombre + valor en cada nivel ────────────────────────────────────
  private funnelOption(): echarts.EChartsOption {
    const { data, labelKey, valueKey } = this.config;
    const hasPct = data.length > 0 && 'Porcentaje_CUMP' in data[0];
    const sorted = [...data]
      .sort((a, b) => Number(b[valueKey!]) - Number(a[valueKey!]))
      .slice(0, 10);
    return {
      tooltip: {
        trigger: 'item',
        formatter: ({ name, value, dataIndex }: any) => {
          const row = sorted[dataIndex];
          const pct = row?.['Porcentaje_CUMP'];
          return `${name}<br/>Aprobados: <b>${fmtCO(value)}</b>`
            + (pct !== undefined ? `<br/>Cumpl.: <b>${(Number(pct) * 100).toFixed(1)}%</b>` : '');
        },
      },
      series: [{
        type: 'funnel', left: '5%', width: '90%', top: 10, bottom: 10, sort: 'descending',
        label: {
          position: 'inside', fontSize: 10, color: '#fff',
          formatter: ({ name, value, dataIndex }: any) => {
            const row = sorted[dataIndex];
            const pct = row?.['Porcentaje_CUMP'];
            const base = `${name.substring(0, 16)}\n${fmtCO(value)}`;
            return hasPct && pct !== undefined
              ? `${base}  (${(Number(pct) * 100).toFixed(0)}%)`
              : base;
          },
        },
        data: sorted.map((r, i) => ({
          name: String(r[labelKey!]),
          value: Number(r[valueKey!]),
          itemStyle: { color: this.p[i % this.p.length] },
        })),
      }],
    };
  }

  // ── Treemap — nombre + valor + % si existe ───────────────────────────────────
  private treemapOption(): echarts.EChartsOption {
    const { data, labelKey, valueKey } = this.config;
    const hasPct = data.length > 0 && 'Porcentaje_CUMP' in data[0];
    return {
      tooltip: {
        formatter: ({ name, value, dataIndex }: any) => {
          const row = data[dataIndex];
          const pct = row?.['Porcentaje_CUMP'];
          return `${name}<br/>Aprobados: <b>${fmtCO(value)}</b>`
            + (pct !== undefined ? `<br/>Cumpl.: <b>${(Number(pct) * 100).toFixed(1)}%</b>` : '');
        },
      },
      series: [{
        type: 'treemap', roam: false, nodeClick: false,
        breadcrumb: { show: false },
        data: data.map((r, i) => ({
          name: String(r[labelKey!]),
          value: Number(r[valueKey!]),
          itemStyle: { color: this.p[i % this.p.length] },
        })),
        label: {
          fontSize: 11,
          formatter: ({ name, value, dataIndex }: any) => {
            const row = data[dataIndex];
            const pct = row?.['Porcentaje_CUMP'];
            const line1 = name.substring(0, 20);
            const line2 = fmtCO(value);
            const line3 = hasPct && pct !== undefined
              ? `${(Number(pct) * 100).toFixed(0)}%` : '';
            return [line1, line2, line3].filter(Boolean).join('\n');
          },
        },
      }],
    };
  }

  // ── Scatter — etiqueta con nombre de misión al hacer hover ───────────────────
  private scatterOption(): echarts.EChartsOption {
    const { data, labelKey, valueKey, value2Key } = this.config;
    const hasPct = data.length > 0 && 'Porcentaje_CUMP' in data[0];
    return {
      tooltip: {
        formatter: (params: any) => {
          const r = data[params.dataIndex];
          const pct = r?.['Porcentaje_CUMP'];
          return `<b>${r[labelKey!]}</b><br>`
            + `${valueKey}: <b>${fmtCO(r[valueKey!])}</b><br>`
            + `${value2Key}: <b>${fmtCO(r[value2Key!])}</b>`
            + (pct !== undefined ? `<br>Cumpl.: <b>${(Number(pct) * 100).toFixed(1)}%</b>` : '');
        },
      },
      grid: { left: 60, right: 20, top: 20, bottom: 40, containLabel: true },
      xAxis: { type: 'value', name: valueKey, nameTextStyle: { fontSize: 10 },
        axisLabel: { fontSize: 10, formatter: fmtK } },
      yAxis: { type: 'value', name: value2Key, nameTextStyle: { fontSize: 10 },
        axisLabel: { fontSize: 10, formatter: fmtK } },
      series: [{
        type: 'scatter',
        symbolSize: (val: any, params: any) => {
          // Tamaño proporcional al % cumplimiento si existe
          const pct = data[params.dataIndex]?.['Porcentaje_CUMP'];
          return pct !== undefined ? 8 + Number(pct) * 16 : 10;
        },
        data: data.map((r, i) => ({
          value: [Number(r[valueKey!]), Number(r[value2Key!])],
          itemStyle: { color: this.p[i % this.p.length], opacity: 0.8 },
        })),
        emphasis: { itemStyle: { shadowBlur: 10 } },
        label: {
          show: false,   // visible sólo al hacer hover vía tooltip
        },
      }],
    };
  }

  // ── Line — valor encima de cada punto ────────────────────────────────────────
  private lineOption(): echarts.EChartsOption {
    const { data, labelKey, valueKey, value2Key } = this.config;
    const pointLabel = {
      show: true, position: 'top' as const, fontSize: 10, fontWeight: 700 as const,
      formatter: ({ value }: any) => fmtK(Number(value)),
    };
    const series: any[] = [{
      name: valueKey, type: 'line', smooth: true,
      data: data.map(r => Number(r[valueKey!])),
      areaStyle: { opacity: 0.15 },
      itemStyle: { color: this.p[0] },
      symbol: 'circle', symbolSize: 5,
      label: pointLabel,
      markLine: value2Key ? undefined : {
        data: [{ type: 'average', name: 'Promedio' }],
        lineStyle: { color: '#ee6666', type: 'dashed' },
        label: { fontSize: 10, formatter: ({ value }: any) => `Prom: ${fmtK(value)}` },
      },
    }];
    if (value2Key) series.push({
      name: value2Key, type: 'line', smooth: true,
      data: data.map(r => Number(r[value2Key])),
      lineStyle: { type: 'dashed' }, itemStyle: { color: this.p[1] },
      symbol: 'circle', symbolSize: 5, label: { ...pointLabel, color: '#3ba272' },
    });
    return {
      tooltip: { trigger: 'axis' },
      legend: value2Key ? { bottom: 0, textStyle: { fontSize: 11 } } : undefined,
      grid: { left: 50, right: 20, top: 36, bottom: value2Key ? 48 : 30, containLabel: true },
      xAxis: { type: 'category', data: data.map(r => r[labelKey!]),
        axisLabel: { fontSize: 10 } },
      yAxis: { type: 'value',
        axisLabel: { fontSize: 10, formatter: fmtK } },
      series,
    };
  }
}
